import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogClose,
} from "@shared/ui/dialog";
import { Button } from "@shared/ui/button";
import { GenericSelect } from "@shared/components/Select";
import React, { useEffect, useMemo, useState } from "react";
import { IconButton } from "@shared/components/IconButton";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import {
  usePutAdjustReadings,
  usePutPassExemptReading,
  useExemptRules,
} from "../../hooks";
import { mapToLabelValue } from "@features/mx/device-management/utils";
import { useValidationContext } from "../../context";
import { toast } from "@shared/hooks/use-toast";
import { logEvent } from "@shared/analytics/analytics";
import { Textarea } from "@shared/ui/textarea";

interface ExemptReadingDialogProps {
  open: boolean;
  onClose: () => void;
  onExempt: (reasonCode: string) => void;
}
export const ExemptReadingDialog: React.FC<ExemptReadingDialogProps> = ({
  open,
  onClose,
  onExempt,
}) => {
  const [selectedReason, setSelectedReason] = useState<string>("");
  const [remark, setRemark] = useState<string>("");
  const remoteUtilityId = useRemoteUtilityId();
  const { readingId, meterRunId, goToNextReading, cycleId } = useValidationContext();
  const { data: exemptRules } = useExemptRules(remoteUtilityId, 0, cycleId);
  const exemptReading = usePutPassExemptReading(readingId, meterRunId);

  const codeList = useMemo(
    () =>
      exemptRules?.map((item: any) => ({
        label: item.rule?.name,
        value: item.id,
      })) || [],
    [exemptRules]
  );

  const handleExempt = () => {
    if (selectedReason) {
      exemptReading.mutate(
        {
          rule_id: selectedReason,
          rule_status: 2,
          remark: remark,
        },
        {
          onSuccess: () => {
            logEvent("MX Reading Exempted", {
              reason: selectedReason,
              remark,
            });
            toast({
              title: "Success",
              description: "Meter exempted successfully.",
              variant: "default",
            });
            goToNextReading();
            setSelectedReason("");
            setRemark("");
            onClose();
          },
        }
      );
    }
  };

  const handleClose = () => {
    setSelectedReason("");
    setRemark("");
    onClose();
  };

  useEffect(() => {
    if (!open) {
      return;
    }
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md rounded-xl p-6">
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold">
            Exempt Reading
          </DialogTitle>
        </DialogHeader>

        <div className="mt-4 space-y-4">
          <div>
            <label
              htmlFor="exemptionRule"
              className="text-sm font-medium block mb-1"
            >
              Exemption Rule <span className="text-destructive">*</span>
            </label>
            <GenericSelect
              placeholder="Select a rule"
              options={codeList}
              value={selectedReason}
              onChange={(val: string) => setSelectedReason(val)}
            />
          </div>

          <div>
            <label
              htmlFor="consumptionRemark"
              className="text-sm font-medium block mb-1"
            >
              Consumption Remark
            </label>
            <Textarea
              id="consumptionRemark"
              placeholder="add a remark about this reading's consumption"
              className="min-h-[80px]"
              value={remark}
              onChange={(e) => setRemark(e.target.value)}
            />
          </div>
        </div>

        <DialogFooter className="mt-6 flex justify-end gap-3">
          <DialogClose asChild>
            <Button variant="secondary" onClick={handleClose}>
              Cancel
            </Button>
          </DialogClose>
          <IconButton
            label="Exempt"
            disabled={!selectedReason}
            onClick={handleExempt}
            className="bg-yellow-400 text-black hover:bg-yellow-400"
          ></IconButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
